/**
 * Formato del campo "clases" del carnet.
 *
 *  - 'codigos' (por defecto; es lo que hacen TODOS los diseños ya guardados): "B1, B2".
 *  - 'codigo_descripcion': una clase por línea, "CÓDIGO - descripción". Si una clase no tiene descripción
 *    se imprime solo el código.
 *
 * En el segundo formato el texto se achica solo, hasta un mínimo ABSOLUTO de 5 pt impresos (calculados sobre el
 * ancho real de la tarjeta, 85,6 mm), para entrar en la caja. Si aun así no entra, se corta con "…" y `cortado`
 * queda en true: el Diseñador lo muestra con un aviso visible y al imprimir se pide confirmación.
 *
 * IMPORTANTE: el cálculo es una función pura de (clases, descripciones, caja, fuente) expresada en
 * "unidades de diseño" (el lienzo de 700 px de ancho, igual que tamano_fuente). No mide el DOM ni depende del
 * tamaño en pantalla: la vista previa, el lienzo del Diseñador, la miniatura y la impresión física llaman a
 * esta misma función con los mismos datos y dibujan exactamente el mismo texto.
 */

import { useEffect, useState } from 'react';
// Medidas de la tarjeta: única fuente en posicionTamano.js (la comparte el panel "Posición y tamaño" del Diseñador).
import { ANCHO_TARJETA_MM, ALTO_TARJETA_MM } from './posicionTamano';

export { ANCHO_TARJETA_MM };

export const FORMATO_CODIGOS = 'codigos';
export const FORMATO_CODIGO_DESCRIPCION = 'codigo_descripcion';

// Mismo valor que CANVAS_ANCHO_DISENO_PX (LicenseCard.jsx / DisenadorEditor.jsx).
export const ANCHO_DISENO_PX = 700;
// ISO/IEC 7810 ID-1: 85,6 x 53,98 mm.
export const ALTO_DISENO_PX = ANCHO_DISENO_PX * (ALTO_TARJETA_MM / ANCHO_TARJETA_MM);
export const INTERLINEADO = 1.15; // el mismo line-height que los textos del carnet

// Tamaño mínimo de letra: 5 pt impresos. 1 pt = 25,4/72 mm; el lienzo de diseño mide 700 px = 85,6 mm.
export const PUNTOS_MINIMOS_IMPRESION = 5;
export const pxDisenoAPuntos = (px) => (px * (ANCHO_TARJETA_MM / ANCHO_DISENO_PX) * 72) / 25.4;
export const puntosAPxDiseno = (pt) => (pt * 25.4 * ANCHO_DISENO_PX) / (72 * ANCHO_TARJETA_MM);
export const MINIMO_FUENTE_PX = puntosAPxDiseno(PUNTOS_MINIMOS_IMPRESION); // ≈ 14,42 px de diseño

const MARGEN_HORIZONTAL_PX = 4; // padding de 2 px a cada lado de la caja
// Margen de seguridad conservador (3 %): a la caja se le descuenta este porcentaje, en ancho y en alto, así una
// diferencia de tipografía o de redondeo entre navegadores nunca esconde un corte (ante la duda, avisa).
export const MARGEN_SEGURIDAD = 0.03;
const SEGURIDAD = 1 - MARGEN_SEGURIDAD;
// Un solo espacio entre el código y la descripción (como imprime Hermoso Campo: "A.1.1 CICLOMOTORES HASTA (50 CC)").
const SEPARADOR = ' ';
const PUNTOS = '…';

export const formatoDeElemento = (elemento) =>
  (elemento && elemento.formato_clases === FORMATO_CODIGO_DESCRIPCION ? FORMATO_CODIGO_DESCRIPCION : FORMATO_CODIGOS);

// --- medición de texto (canvas; deterministas para un mismo tipo de letra) ---------------------------------
let ctxMedicion = null;
let familiaCache = null;
const familiaTipografica = () => {
  if (familiaCache) return familiaCache;
  let familia = '';
  try {
    familia = getComputedStyle(document.documentElement).getPropertyValue('--font-body').trim();
  } catch { /* sin DOM (tests) */ }
  familiaCache = familia || "'Inter', sans-serif";
  return familiaCache;
};

/** Ancho en px de `texto` con letra de 1 px (el ancho escala linealmente con el tamaño). */
const anchoPorPx = (texto, negrita, cursiva) => {
  try {
    if (!ctxMedicion) ctxMedicion = document.createElement('canvas').getContext('2d');
    if (ctxMedicion) {
      ctxMedicion.font = `${cursiva ? 'italic ' : ''}${negrita ? 800 : 600} 100px ${familiaTipografica()}`;
      return ctxMedicion.measureText(texto).width / 100;
    }
  } catch { /* sin canvas: estimación */ }
  return texto.length * 0.6;
};

/** Líneas de texto del campo, una por clase. */
export const lineasDeClases = (clases, catalogo) => (Array.isArray(clases) ? clases : [])
  .filter((c) => typeof c === 'string' && c)
  .map((codigo) => {
    const d = catalogo?.porCodigo?.[codigo]?.descripcion;
    return d ? `${codigo}${SEPARADOR}${d}` : codigo;
  });

/** Corta `linea` con "…" para que entre en `anchoMax` (px de diseño) a `fontPx`. */
const recortarLinea = (linea, anchoMax, fontPx, negrita, cursiva) => {
  const entra = (t) => anchoPorPx(t, negrita, cursiva) * fontPx <= anchoMax;
  if (entra(linea)) return linea;
  let n = linea.length;
  while (n > 1 && !entra(`${linea.slice(0, n).trimEnd()}${PUNTOS}`)) n -= 1;
  return `${linea.slice(0, n).trimEnd()}${PUNTOS}`;
};

/**
 * Resuelve el texto de un campo "clases" en formato 'codigo_descripcion'.
 * Devuelve { lineas, fontPx, reducido, cortado, minimoPx, baseFontPx }:
 *  - lineas: las líneas finales (ya cortadas con "…" si hizo falta);
 *  - fontPx: tamaño final en px de diseño (700 px de ancho de lienzo);
 *  - reducido: true si se achicó respecto del tamaño elegido;
 *  - cortado: true si ni al mínimo entra y se cortó texto.
 */
export const resolverClasesCodigoDescripcion = ({ clases, catalogo, elemento }) => {
  const baseFontPx = Math.max(6, Number(elemento.tamano_fuente) || 12);
  const negrita = !!elemento.negrita;
  const cursiva = !!elemento.cursiva;
  // Mínimo absoluto de 5 pt (si el diseñador eligió una letra menor, esa letra es su propio tope: no se agranda).
  const minimoPx = Math.min(baseFontPx, MINIMO_FUENTE_PX);
  const anchoDisp = Math.max(1, ((Number(elemento.width) || 0) / 100) * ANCHO_DISENO_PX - MARGEN_HORIZONTAL_PX) * SEGURIDAD;
  const altoDisp = Math.max(1, ((Number(elemento.height) || 0) / 100) * ALTO_DISENO_PX) * SEGURIDAD;

  const lineas = lineasDeClases(clases, catalogo);
  if (lineas.length === 0) return { lineas: [], fontPx: baseFontPx, reducido: false, cortado: false, minimoPx, baseFontPx };

  const anchoMaxPorPx = Math.max(...lineas.map((l) => anchoPorPx(l, negrita, cursiva)));
  const fuenteParaAncho = anchoMaxPorPx > 0 ? anchoDisp / anchoMaxPorPx : baseFontPx;
  const fuenteParaAlto = altoDisp / (lineas.length * INTERLINEADO);
  const ideal = Math.min(baseFontPx, fuenteParaAncho, fuenteParaAlto);

  if (ideal >= minimoPx) {
    const fontPx = Math.floor(ideal * 10) / 10;
    return { lineas, fontPx, reducido: fontPx < baseFontPx, cortado: false, minimoPx, baseFontPx };
  }

  // No entra ni al mínimo: se usa el mínimo, se muestran las líneas que caben en alto y se corta lo ancho.
  const fontPx = Math.floor(minimoPx * 10) / 10;
  const maxLineas = Math.max(1, Math.floor(altoDisp / (fontPx * INTERLINEADO)));
  let visibles = lineas.slice(0, maxLineas);
  let cortado = lineas.length > maxLineas;
  visibles = visibles.map((l, i) => {
    const esUltimaCortada = cortado && i === visibles.length - 1;
    const base = esUltimaCortada ? `${l}${PUNTOS}` : l;
    const recortada = recortarLinea(base, anchoDisp, fontPx, negrita, cursiva);
    if (recortada !== base) cortado = true;
    return recortada;
  });
  return { lineas: visibles, fontPx, reducido: true, cortado, minimoPx, baseFontPx };
};

/** Texto plano del campo en formato 'codigos' ("B1, B2"): el comportamiento de siempre. */
export const textoClasesSoloCodigos = (clases) => (Array.isArray(clases) ? clases : []).join(', ');

/** Conversión de px de diseño a la unidad de fuente de los carnets (cqw, relativo al ancho de la tarjeta). */
export const pxDisenoACqw = (px) => `${(px / ANCHO_DISENO_PX) * 100}cqw`;

/**
 * ¿Algún campo "clases" en formato código + descripción de este diseño se corta con "…" para estas clases?
 * Es la misma cuenta que el lienzo del Diseñador y la impresión (se usa en la galería y antes de imprimir).
 * `caras`: 'frente' | 'dorso' | 'ambas'.
 */
export const clasesSeCortanEnDiseno = (diseno, clases, catalogo, caras = 'ambas') => {
  if (!diseno) return false;
  const capas = [];
  if (caras !== 'dorso') capas.push(diseno.layout_frente);
  if (caras !== 'frente') capas.push(diseno.layout_dorso);
  return capas
    .flatMap((l) => (Array.isArray(l) ? l : []))
    .some((el) => (
      el && el.tipo === 'texto' && el.fuente_dato === 'clases' && formatoDeElemento(el) === FORMATO_CODIGO_DESCRIPCION
      && resolverClasesCodigoDescripcion({ clases, catalogo, elemento: el }).cortado
    ));
};

// --- tipografía --------------------------------------------------------------------------------------------------
// Inter (y Montserrat) se piden a Google Fonts (public/index.html). El cálculo de arriba mide con la letra que el
// navegador tenga en ese momento: si todavía no llegó la web font, mide con una letra de reemplazo (Segoe UI,
// Arial, DejaVu... según el sistema) y el resultado puede diferir de un equipo a otro. Por eso se recalcula cada
// vez que el navegador termina de cargar tipografías (evento "loadingdone"), cuando se completa la página, y
// cuando document.fonts.ready se resuelve.
const TEXTO_DE_CARGA = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789ÁÉÍÓÚÑáéíóúñ.,()-/';

const paginaCompleta = () => new Promise((resolver) => {
  if (document.readyState === 'complete') resolver();
  else window.addEventListener('load', () => resolver(), { once: true });
});

/** Espera a que la página y las tipografías (Inter 600 y 800, con tildes) estén cargadas. Nunca rechaza. */
export const esperarTipografias = () => {
  try {
    if (document.fonts && document.fonts.load) {
      return paginaCompleta()
        .then(() => Promise.all([
          document.fonts.load('600 16px Inter', TEXTO_DE_CARGA),
          document.fonts.load('800 16px Inter', TEXTO_DE_CARGA),
        ]))
        .catch(() => null)
        .then(() => document.fonts.ready)
        .then(() => undefined, () => undefined);
    }
  } catch { /* sin API de fuentes */ }
  return Promise.resolve();
};

/**
 * ¿Se está usando de verdad la tipografía Inter? Compara el ancho de un texto de prueba con Inter contra el de la
 * letra de respaldo sola: si son iguales, Inter no está disponible (sin internet, bloqueada, o todavía cargando).
 */
export const interDisponible = () => {
  try {
    if (!ctxMedicion) ctxMedicion = document.createElement('canvas').getContext('2d');
    if (!ctxMedicion) return true;
    const prueba = 'Mmmmmmmmllliii 0123456789';
    ctxMedicion.font = '600 100px Inter, monospace';
    const conInter = ctxMedicion.measureText(prueba).width;
    ctxMedicion.font = '600 100px monospace';
    return conInter !== ctxMedicion.measureText(prueba).width;
  } catch { return true; }
};

/** Devuelve un número que cambia cada vez que las tipografías terminan de cargarse: fuerza a recalcular. */
export const useTipografiasListas = () => {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let vivo = true;
    const recalcular = () => { if (vivo) setVersion((v) => v + 1); };
    esperarTipografias().then(recalcular);
    const fuentes = typeof document !== 'undefined' ? document.fonts : null;
    if (fuentes && fuentes.addEventListener) fuentes.addEventListener('loadingdone', recalcular);
    window.addEventListener('load', recalcular);
    return () => {
      vivo = false;
      if (fuentes && fuentes.removeEventListener) fuentes.removeEventListener('loadingdone', recalcular);
      window.removeEventListener('load', recalcular);
    };
  }, []);
  return version;
};
